import { Decimal } from '../../datastructures';
import { Nullable, Token } from '../../interfaces';
import nostraTokenBalancesAtom from './nostraTokenBalanceAtom';
import accountsAtom from './accountsAtom';
import { walletAddressAtom } from '../wallet';
import nostraTokenListAtom from './nostraTokenListAtom';
import assetTokenRatesAtom from './assetTokenRatesAtom';
import { getConfigManager } from '../../config';
import { createAsyncAtom } from '../../lib/state';
import { ASSETS } from '../../constants';

type PerTokenTotalSupply = {
  [token in Token]: Nullable<Decimal>;
};

interface PerTokenAccountTotalSupply {
  [walletAddress: string]: PerTokenTotalSupply;
}

export interface AccountTotalSupplyMap {
  [walletAddressAccountId: string]: Decimal;
}

const accountTotalSupplyAtom = createAsyncAtom(async get => {
  const nostraTokenBalances = await get(nostraTokenBalancesAtom);
  const accounts = await get(accountsAtom);
  const walletAddress = await get(walletAddressAtom);
  const nostraTokenList = await get(nostraTokenListAtom);
  const assetTokenRates = await get(assetTokenRatesAtom);

  if (!walletAddress) {
    return null;
  }

  const configManager = getConfigManager();

  const perTokenAccountTotalSupplyMap: PerTokenAccountTotalSupply = {};
  const accountTotalSupplyMap: AccountTotalSupplyMap = {};

  accounts.forEach((subAccountName, accountId) => {
    if (!subAccountName) {
      return;
    }

    nostraTokenList.forEach(nostraToken => {
      try {
        // Do not count non lend tokens when calculating total supply for a account
        if (nostraToken.variant === 'debt' || !nostraToken.variant.interestBearing) {
          return;
        }

        const accountTotalSupplyKey = `${walletAddress}-${accountId}`;
        const tokenBalanceKey = `${walletAddress}-${accountId}-${nostraToken.address}`;
        const tokenRate =
          assetTokenRates.rate[configManager.getNostraTokenAssetAddress(nostraToken.address)] ?? Decimal.ONE;
        const tokenBalance = nostraTokenBalances[tokenBalanceKey];

        if (!perTokenAccountTotalSupplyMap[walletAddress]) {
          perTokenAccountTotalSupplyMap[walletAddress] = ASSETS.reduce(
            (map, asset) => ({ ...map, [asset]: null }),
            {} as PerTokenTotalSupply,
          );
        }

        if (!tokenBalance) {
          return;
        }

        perTokenAccountTotalSupplyMap[walletAddress][nostraToken.underlyingToken] = (
          perTokenAccountTotalSupplyMap[walletAddress][nostraToken.underlyingToken] ?? Decimal.ZERO
        ).add(tokenBalance);

        accountTotalSupplyMap[accountTotalSupplyKey] = (
          accountTotalSupplyMap[accountTotalSupplyKey] ?? Decimal.ZERO
        ).add(tokenBalance.mul(tokenRate));
      } catch (error) {
        console.error(`useAccountTotalSupply - error calculating total supply for ID ${accountId}`, error);
      }
    });
  });

  return {
    perToken: perTokenAccountTotalSupplyMap,
    total: accountTotalSupplyMap,
  };
});

export default accountTotalSupplyAtom;
